import type { Adb } from "@yume-chan/adb";
import type { AppDispatch } from "../../../common/state/store";
import { getConnection } from "./adbConnection";
import { shellCmd } from "./adbShell";
import { createMediaEncryptionStream } from "./mediaEncryption";
import { updateMediaPushProgress } from "../state/deviceInstallerSlice";
import { getMediaBasePath, MEDIA_METADATA_FILES } from "../constants";
import {
  createProgressStream,
  isAbortError,
  openDownloadStream,
  pushFileToDevice,
  pushStreamToDevice,
} from "./adbHelpers";
import type { MediaFileToPush, MediaManifestItem, MediaPushTarget } from "./mediaFiles";
import { getMediaFilesToPush } from "./mediaFiles";

export type { MediaManifestItem } from "./mediaFiles";

/** Push a small text file to the device */

async function pushTextFile(adb: Adb, path: string, content: string): Promise<void> {
  await pushFileToDevice(adb, path, new TextEncoder().encode(content));
}

async function moveOnDevice(adb: Adb, from: string, to: string): Promise<void> {
  const output = await shellCmd(adb, ["mv", "-f", from, to]);
  if (output.trim() !== "") {
    throw new Error(`mv ${from} failed: ${output.trim()}`);
  }
}

async function grantAppAccess(adb: Adb, path: string): Promise<void> {
  await shellCmd(adb, ["chmod", "-R", "a+rwX", path]);
}

/** Read a small text file from the device, returns null if not found */
async function readTextFile(adb: Adb, path: string): Promise<string | null> {
  try {
    const output = await shellCmd(adb, ["cat", path]);
    if (output.includes("No such file")) return null;
    return output.trim();
  } catch (err) {
    console.warn(`[ADB] readTextFile(${path}) failed:`, err);
    return null;
  }
}

async function listDir(adb: Adb, dir: string): Promise<Set<string>> {
  try {
    const output = await shellCmd(adb, ["ls", "-1", dir]);
    if (output.includes("No such file")) return new Set();
    return new Set(
      output
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean),
    );
  } catch (err) {
    console.warn(`[ADB] listDir(${dir}) failed:`, err);
    return new Set();
  }
}

async function isMediaUpToDate(
  adb: Adb,
  mediaDir: string,
  media: MediaManifestItem,
  files: MediaFileToPush[],
): Promise<boolean> {
  const present = await listDir(adb, mediaDir);
  if (!present.has(MEDIA_METADATA_FILES.updatedTime)) return false;
  if (!files.every((f) => present.has(f.filename))) return false;
  const storedTimestamp = await readTextFile(
    adb,
    `${mediaDir}/${MEDIA_METADATA_FILES.updatedTime}`,
  );
  return (
    storedTimestamp !== null && media.updatedAt !== null && storedTimestamp === media.updatedAt
  );
}

const RM_BATCH_SIZE = 40;

async function removeDevicePaths(adb: Adb, paths: string[]): Promise<boolean> {
  for (let i = 0; i < paths.length; i += RM_BATCH_SIZE) {
    const output = await shellCmd(adb, ["rm", "-rf", ...paths.slice(i, i + RM_BATCH_SIZE)]);
    if (output.trim() !== "") {
      console.warn("[ADB] rm reported:", output.trim());
      return false;
    }
  }
  return true;
}

/** Delete all pre-loaded media from the device for the given package */
export async function clearAllMediaFromDevice(packageName: string): Promise<boolean> {
  const adb = getConnection();
  if (!adb) return false;

  try {
    return await removeDevicePaths(adb, [getMediaBasePath(packageName)]);
  } catch (err) {
    console.warn(`[ADB] clearAllMediaFromDevice(${packageName}) failed:`, err);
    return false;
  }
}

export async function clearMediaSelectionFromDevice(
  packageName: string,
  mediaIds: number[],
): Promise<boolean> {
  const adb = getConnection();
  if (!adb) return false;

  const basePath = getMediaBasePath(packageName);
  const paths = mediaIds
    .filter((id) => Number.isSafeInteger(id) && id > 0)
    .map((id) => `${basePath}/${id}`);
  try {
    return await removeDevicePaths(adb, paths);
  } catch (err) {
    console.warn(`[ADB] clearMediaSelectionFromDevice(${packageName}) failed:`, err);
    return false;
  }
}

export interface MediaPushResult {
  pushed: number;
  failed: number;
  skipped: number;
}

/** Check which medias already exist and are up-to-date on the device */
export async function scanDeviceMediaStatus(
  packageName: string,
  medias: MediaManifestItem[],
  target: MediaPushTarget,
): Promise<Map<number, boolean>> {
  const adb = getConnection();
  const result = new Map<number, boolean>();
  if (!adb) return result;

  const basePath = getMediaBasePath(packageName);

  for (const media of medias) {
    const files = getMediaFilesToPush(media, target);
    const upToDate =
      files.length > 0 && (await isMediaUpToDate(adb, `${basePath}/${media.id}`, media, files));
    result.set(media.id, upToDate);
  }

  return result;
}

/**
 * Push all media files to the device's persistentDataPath cache.
 * Processes files sequentially to limit memory usage.
 */
export async function pushMediasToDevice(
  packageName: string,
  medias: MediaManifestItem[],
  dispatch: AppDispatch,
  signal: AbortSignal,
  target: MediaPushTarget,
): Promise<MediaPushResult> {
  const adb = getConnection();
  if (!adb) throw new Error("No ADB connection");

  const basePath = getMediaBasePath(packageName);
  const result: MediaPushResult = { pushed: 0, failed: 0, skipped: 0 };

  await shellCmd(adb, ["mkdir", "-p", basePath]);
  await grantAppAccess(adb, basePath);

  for (let i = 0; i < medias.length; i++) {
    if (signal.aborted) throw new DOMException("Cancelled", "AbortError");

    const media = medias[i];
    const files = getMediaFilesToPush(media, target);

    if (files.length === 0) {
      result.skipped++;
      continue;
    }

    const displayName = media.name || files[0].filename;
    const mediaDir = `${basePath}/${media.id}`;

    dispatch(
      updateMediaPushProgress({
        pushedFiles: i,
        currentFileName: displayName,
        currentFileProgress: 0,
      }),
    );

    try {
      if (await isMediaUpToDate(adb, mediaDir, media, files)) {
        result.skipped++;
        continue;
      }

      await shellCmd(adb, ["mkdir", "-p", mediaDir]);

      for (let f = 0; f < files.length; f++) {
        const file = files[f];
        const { stream, totalSize } = await openDownloadStream(file.url, signal);
        const tracked = stream.pipeThrough(
          createProgressStream(totalSize || file.sizeInBytes, (percent) => {
            dispatch(
              updateMediaPushProgress({
                pushedFiles: i,
                currentFileName: displayName,
                currentFileProgress: Math.round(((f + percent / 100) / files.length) * 100),
              }),
            );
          }),
        );
        const body = file.encrypt ? tracked.pipeThrough(createMediaEncryptionStream()) : tracked;

        const finalPath = `${mediaDir}/${file.filename}`;
        await pushStreamToDevice(adb, `${finalPath}.part`, body);
        if (signal.aborted) throw new DOMException("Cancelled", "AbortError");
        await moveOnDevice(adb, `${finalPath}.part`, finalPath);
      }

      await pushTextFile(
        adb,
        `${mediaDir}/${MEDIA_METADATA_FILES.encrypted}`,
        files[0].encrypt ? "True" : "False",
      );
      await pushTextFile(
        adb,
        `${mediaDir}/${MEDIA_METADATA_FILES.updatedTime}`,
        media.updatedAt || "",
      );
      await pushTextFile(adb, `${mediaDir}/${MEDIA_METADATA_FILES.presets}`, "");
      await grantAppAccess(adb, mediaDir);

      result.pushed++;
    } catch (err: unknown) {
      if (isAbortError(err)) throw err;
      if (signal.aborted) throw new DOMException("Cancelled", "AbortError");
      console.error(`Failed to push media ${media.id} (${displayName}):`, err);
      result.failed++;
    }
  }

  dispatch(
    updateMediaPushProgress({
      pushedFiles: medias.length,
      currentFileName: "",
      currentFileProgress: 100,
    }),
  );

  return result;
}
